import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { useWorkflow } from "../contexts/WorkflowContext";
import { WorkflowType } from "../api/athena";

export type StepKey =
  | "personal-information"
  | "contact-and-communication"
  | "address-and-identity"
  | "emergency-and-billing"
  | "care-and-insurance"
  | "medical-history"
  | "wellness-check"
  | "review-and-sign";

interface StepDefinition {
  key: StepKey;
  label: string;
}

// Keep for backward compatibility/fallback
const STEP_DEFINITIONS: StepDefinition[] = [
  { key: "personal-information", label: "Personal Information" },
  { key: "contact-and-communication", label: "Contact & Communication" },
  { key: "address-and-identity", label: "Address & Identity" },
  { key: "emergency-and-billing", label: "Emergency & Billing" },
  { key: "care-and-insurance", label: "Care & Insurance" },
  { key: "medical-history", label: "Health History" },
  { key: "wellness-check", label: "Wellness Check" },
  { key: "review-and-sign", label: "Agreement & Sign" },
];

export function useAppointmentBasePath(): string {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  return appointmentId ? `/appointments/${appointmentId}` : "";
}

export function useAppointmentSteps(activeKey?: StepKey) {
  const basePath = useAppointmentBasePath();
  const location = useLocation();
  const { workflow, loading, error } = useWorkflow();

  // Use React.useMemo to prevent re-renders when workflow is still loading
  // Return stable fallback steps until workflow is fully loaded
  return React.useMemo(() => {
    // If workflow has error, don't show steps
    if (error) {
      return [];
    }

    // If workflow is loading or no workflow, return fallback steps
    // This prevents the steps from changing when workflow loads
    if (loading || !workflow?.steps || workflow.steps.length === 0) {
      // Fallback to static steps if workflow not available
      if (activeKey) {
        return STEP_DEFINITIONS.map((step) => ({
          label: step.label,
          active: step.key === activeKey,
          path: basePath ? `${basePath}/${step.key}` : `/${step.key}`,
        }));
      }
      return [];
    }

  // Fixed workflow type order
  const typeOrder = [
    WorkflowType.DEMOGRAPHIC,
    WorkflowType.HEALTH_HISTORY_FORM,
    WorkflowType.SCREENERS,
    WorkflowType.CONSENT_FORM,
  ];

  // Group steps by workflow type
  const stepsByType = new Map<WorkflowType, typeof workflow.steps>();
  workflow.steps.forEach(step => {
    if (!stepsByType.has(step.workflowType)) {
      stepsByType.set(step.workflowType, []);
    }
    stepsByType.get(step.workflowType)!.push(step);
  });

  // Sort steps within each type by displayOrder
  stepsByType.forEach((steps) => {
    steps.sort((a, b) => a.displayOrder - b.displayOrder);
  });

  // Get unique stepUrls in order, maintaining workflow type priority
  const uniqueStepUrls: Array<{ url: string; step: typeof workflow.steps[0] }> = [];
  const seenUrls = new Set<string>();

  for (const type of typeOrder) {
    const steps = stepsByType.get(type) || [];
    for (const step of steps) {
      if (!seenUrls.has(step.stepUrl)) {
        uniqueStepUrls.push({ url: step.stepUrl, step });
        seenUrls.add(step.stepUrl);
      }
    }
  }

  // Determine active step
  let activeStepUrl: string | undefined;
  const currentPath = location.pathname;

  // Try to match current route with stepUrl
  const matchedStep = uniqueStepUrls.find(({ url }) => url === currentPath);
  if (matchedStep) {
    activeStepUrl = matchedStep.url;
  } else if (activeKey) {
    // Fallback to activeKey if provided
    const fallbackStep = uniqueStepUrls.find(({ step }) => {
      const urlParts = step.stepUrl.split('/');
      const lastPart = urlParts[urlParts.length - 1];
      return lastPart === activeKey;
    });
    if (fallbackStep) {
      activeStepUrl = fallbackStep.url;
    }
  }

  // If no match, use first pending step
  if (!activeStepUrl) {
    const firstPending = workflow.steps.find(step => 
      step.status === 'PENDING' || step.status === 'IN_PROGRESS'
    );
    if (firstPending) {
      activeStepUrl = firstPending.stepUrl;
    }
  }

  // Helper function to get hardcoded label from route key
  const getLabelFromRouteKey = (url: string): string => {
    const urlParts = url.split('/');
    const routeKey = urlParts[urlParts.length - 1] as StepKey;
    const stepDef = STEP_DEFINITIONS.find(def => def.key === routeKey);
    return stepDef?.label || routeKey;
  };

    // Map to ProgressStep format - always use hardcoded labels
    return uniqueStepUrls.map(({ url, step }) => ({
      label: getLabelFromRouteKey(url),
      active: url === activeStepUrl,
      path: url,
    }));
  }, [workflow, loading, error, activeKey, basePath, location.pathname]);
}

export function useAppointmentStepPath(key: StepKey): string {
  const basePath = useAppointmentBasePath();
  const { workflow } = useWorkflow();

  // Try to find step in workflow by route key
  if (workflow?.steps) {
    const step = workflow.steps.find(step => {
      const urlParts = step.stepUrl.split('/');
      const lastPart = urlParts[urlParts.length - 1];
      return lastPart === key;
    });
    if (step) {
      return step.stepUrl;
    }
  }

  // Fallback to constructed path
  return basePath ? `${basePath}/${key}` : `/${key}`;
}
